
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, BarChart2, Link as LinkIcon, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PollTable from './polls/PollTable';
import PollForm from './polls/PollForm';
import { usePollManagement } from '@/hooks/usePollManagement';
import PollPreview from '@/components/admin/polls/PollPreview';
import { Poll } from '@/types/poll';
import LinkPollToCardDialog from './polls/LinkPollToCardDialog';
import { recalculateAllLinkedCards } from '@/utils/polls/pollAdminUtils';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import PollsAdminControls from './polls/PollsAdminControls';
import PollsPagination from './polls/PollsPagination';
import { usePagination } from '@/hooks/polls/usePagination';

const PollDataManagement = () => {
  const {
    polls,
    isLoading,
    dialogOpen,
    setDialogOpen,
    currentPoll,
    setCurrentPoll,
    isEditing,
    handleOpenDialog,
    handleSavePoll,
    handleDeletePoll,
    handleActivatePoll,
    handleClosePoll,
    handleReopenPoll,
    linkPollToCard,
    refetchPolls
  } = usePollManagement();

  console.log('PollDataManagement rendering:', {
    pollsLoaded: polls.length,
    dialogOpen,
    isEditing,
    currentPollId: currentPoll?.id
  });

  const isMobile = useIsMobile();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPoll, setPreviewPoll] = useState<Poll | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [pollToLink, setPollToLink] = useState<Poll | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // State for filtered polls
  const [filteredPolls, setFilteredPolls] = useState<Poll[]>([]);
  
  // Pagination
  const pagination = usePagination({
    totalItems: filteredPolls.length,
    initialPage: 1,
    itemsPerPage: 10
  });
  
  // Get paginated polls
  const paginatedPolls = pagination.paginatedItems(filteredPolls);
  
  // Update filtered polls whenever the original polls change
  useEffect(() => {
    setFilteredPolls(polls);
  }, [polls]);

  // Log component mount but don't trigger a fetch here - usePollManagement handles that
  useEffect(() => {
    console.log('PollDataManagement mounted');
  }, []);

  const handlePreviewPoll = (poll: Poll) => {
    console.log('Opening preview for poll:', poll.id);
    setPreviewPoll(poll);
    setPreviewOpen(true);
  };

  const handleOpenLinkDialog = (poll: Poll) => {
    console.log('Opening link dialog for poll:', poll.id);
    setPollToLink(poll);
    setLinkDialogOpen(true);
  };

  const handleLinkPoll = (cardId: string) => {
    if (pollToLink) {
      console.log(`Linking poll ${pollToLink.id} to card ${cardId}`);
      linkPollToCard(pollToLink.id, cardId);
      setLinkDialogOpen(false);
    }
  };

  const handleRecalculateStats = async () => {
    try {
      setIsRecalculating(true);
      await recalculateAllLinkedCards();
      await refetchPolls(); // Ensure polls are refreshed after recalculating
    } catch (error) {
      console.error("Error recalculating statistics:", error);
      toast.error("Failed to recalculate statistics");
    } finally {
      setIsRecalculating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart2 size={20} />
            Poll Management
          </CardTitle>
          <CardDescription>
            Create and manage polls to gather opinions from citizens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`mb-4 ${isMobile ? 'flex flex-col gap-2' : 'flex justify-between'}`}>
            <PollsAdminControls 
              polls={polls}
              setFilteredPolls={setFilteredPolls}
              onRecalculate={handleRecalculateStats}
              isRecalculating={isRecalculating}
            />
            <Button 
              onClick={() => handleOpenDialog()}
              className={isMobile ? 'w-full mt-2' : ''}
            >
              <Plus size={16} className="mr-2" />
              Create New Poll
            </Button>
          </div>
          
          <PollTable 
            polls={paginatedPolls} 
            isLoading={isLoading} 
            onEdit={handleOpenDialog}
            onDelete={handleDeletePoll}
            onActivate={handleActivatePoll}
            onClose={handleClosePoll}
            onReopen={handleReopenPoll}
            onPreview={handlePreviewPoll}
            onLinkToCard={handleOpenLinkDialog}
          />
          
          {filteredPolls.length > 0 && (
            <PollsPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={filteredPolls.length}
              startIndex={pagination.startIndex}
              endIndex={pagination.endIndex}
              onPageChange={pagination.setCurrentPage}
              onItemsPerPageChange={pagination.setItemsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Poll Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95%] p-4' : 'sm:max-w-[720px]'}`}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit' : 'Create'} Poll</DialogTitle>
          </DialogHeader>
          
          <PollForm
            currentPoll={currentPoll}
            setCurrentPoll={setCurrentPoll}
            onCancel={() => setDialogOpen(false)}
            onSave={handleSavePoll}
            isEditing={isEditing}
          />
        </DialogContent>
      </Dialog>

      {/* Poll Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`${isMobile ? 'w-[95%] p-4' : 'sm:max-w-[600px]'}`}>
          <DialogHeader>
            <DialogTitle>Poll Preview</DialogTitle>
          </DialogHeader>
          {previewPoll && <PollPreview poll={previewPoll} />}
        </DialogContent>
      </Dialog>

      {/* Link Poll to Card Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95%] p-4' : 'sm:max-w-[600px]'}`}>
          <DialogHeader>
            <DialogTitle>Link Poll to Insight Card</DialogTitle>
          </DialogHeader>
          <LinkPollToCardDialog 
            poll={pollToLink} 
            onLink={handleLinkPoll} 
            onCancel={() => setLinkDialogOpen(false)} 
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PollDataManagement;
